/**
 * Customer authentication API (OTP-based via Twilio Verify).
 *
 * Endpoints (verified against /openapi.json):
 *   POST /api/v1/customer/auth/login/init    → trigger OTP (returns expiry seconds)
 *   POST /api/v1/customer/auth/login/verify  → exchange OTP for tokens
 *   POST /api/v1/customer/auth/signup/init   → trigger signup OTP
 *   POST /api/v1/customer/auth/signup/verify → exchange OTP + create account → tokens
 *   GET  /api/v1/auth/me                     → current user
 *   POST /api/v1/auth/refresh                → handled transparently by api-client
 *
 * NOTE: backend has no /logout endpoint. `useLogout()` clears local state only.
 */
import { useMutation, useQuery, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { ApiError, createKeyFactory, type Schemas } from '@finmin/api-client';
import type { AuthUser } from '@finmin/auth';
import { apiClient, tokenStore } from '@/lib/api';
import { queryClient } from './query-client';

// ---------- types (from generated schema) -----------------------------------

export type CustomerLoginInitRequest = Schemas['CustomerLoginInitRequest'];
export type CustomerLoginVerifyRequest = Schemas['CustomerLoginVerifyRequest'];
export type CustomerSignupInitRequest = Schemas['CustomerSignupInitRequest'];
export type CustomerSignupVerifyRequest = Schemas['CustomerSignupVerifyRequest'];
export type CustomerInitResponse = Schemas['CustomerInitResponse'];
export type CustomerVerifyResponse = Schemas['CustomerVerifyResponse'];

// ---------- query keys ------------------------------------------------------

export const authKeys = createKeyFactory('auth', {
  me: () => ['me'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** POST /api/v1/customer/auth/login/init — sends a 6-digit OTP to mobile_e164. */
export async function initCustomerLogin(payload: CustomerLoginInitRequest) {
  const { data } = await apiClient.post<CustomerInitResponse>(
    '/api/v1/customer/auth/login/init',
    payload,
  );
  return data;
}

/** POST /api/v1/customer/auth/login/verify — exchanges OTP for access+refresh+firestore tokens. */
export async function verifyCustomerLogin(payload: CustomerLoginVerifyRequest) {
  const { data } = await apiClient.post<CustomerVerifyResponse>(
    '/api/v1/customer/auth/login/verify',
    payload,
  );
  return data;
}

/** POST /api/v1/customer/auth/signup/init — sends OTP for new account creation. */
export async function initCustomerSignup(payload: CustomerSignupInitRequest) {
  const { data } = await apiClient.post<CustomerInitResponse>(
    '/api/v1/customer/auth/signup/init',
    payload,
  );
  return data;
}

/** POST /api/v1/customer/auth/signup/verify — verifies OTP, creates customer, returns tokens. */
export async function verifyCustomerSignup(payload: CustomerSignupVerifyRequest) {
  const { data } = await apiClient.post<CustomerVerifyResponse>(
    '/api/v1/customer/auth/signup/verify',
    payload,
  );
  return data;
}

/** GET /api/v1/auth/me — returns the authenticated customer. */
export async function fetchMe(signal?: AbortSignal) {
  const { data } = await apiClient.get<AuthUser>('/api/v1/auth/me', { signal });
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useMe(options?: Omit<UseQueryOptions<AuthUser, ApiError>, 'queryKey' | 'queryFn'>) {
  return useQuery<AuthUser, ApiError>({
    queryKey: authKeys.me(),
    queryFn: ({ signal }) => fetchMe(signal),
    ...options,
  });
}

export function useInitLogin(
  options?: UseMutationOptions<CustomerInitResponse, ApiError, CustomerLoginInitRequest>,
) {
  return useMutation<CustomerInitResponse, ApiError, CustomerLoginInitRequest>({
    mutationFn: initCustomerLogin,
    ...options,
  });
}

/** Mutation: verify the OTP and persist all 3 tokens. */
export function useVerifyLogin(
  options?: UseMutationOptions<CustomerVerifyResponse, ApiError, CustomerLoginVerifyRequest>,
) {
  return useMutation<CustomerVerifyResponse, ApiError, CustomerLoginVerifyRequest>({
    // Spread user options first so our internal handlers always win (token
    // storage must happen before any navigation side-effect in onSuccess).
    ...options,
    mutationFn: verifyCustomerLogin,
    onSuccess: (data, vars, ctx, mutation) => {
      tokenStore.setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        firestoreToken: data.firestore_token,
      });
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      options?.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}

export function useInitSignup(
  options?: UseMutationOptions<CustomerInitResponse, ApiError, CustomerSignupInitRequest>,
) {
  return useMutation<CustomerInitResponse, ApiError, CustomerSignupInitRequest>({
    mutationFn: initCustomerSignup,
    ...options,
  });
}

export function useVerifySignup(
  options?: UseMutationOptions<CustomerVerifyResponse, ApiError, CustomerSignupVerifyRequest>,
) {
  return useMutation<CustomerVerifyResponse, ApiError, CustomerSignupVerifyRequest>({
    // Same ordering fix as useVerifyLogin — internal token storage must run
    // before any caller-provided onSuccess side-effect.
    ...options,
    mutationFn: verifyCustomerSignup,
    onSuccess: (data, vars, ctx, mutation) => {
      tokenStore.setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        firestoreToken: data.firestore_token,
      });
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      options?.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}
